import { Link, router, useForm, usePage } from "@inertiajs/react"
import { Plus, UserPlus, X } from "lucide-react"
import { type ReactNode, useState } from "react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { AdminLayout } from "@/layouts/AdminLayout"
import type {
  DistributorRow,
  RegionOption,
} from "@/lib/types"
import type { SharedProps } from "@/types"

interface Props {
  distributors: DistributorRow[]
  all_regions: RegionOption[]
  urls: { invite: string; assign: string; remove_assignment: string }
}

// ---------- Invite Sheet ----------

function InviteSheet({
  open,
  onClose,
  allRegions,
  inviteUrl,
}: {
  open: boolean
  onClose: () => void
  allRegions: RegionOption[]
  inviteUrl: string
}) {
  const { errors } = usePage<SharedProps>().props
  const { data, setData, post, processing, reset } = useForm({
    first_name: "",
    last_name:  "",
    email:      "",
    region_id:  "",
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post(inviteUrl, {
      onSuccess: () => {
        reset()
        onClose()
      },
    })
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Invite regional distributor</SheetTitle>
          <SheetDescription>
            Enter their details and choose a starting region. They will receive an invitation
            email to set up their account.
          </SheetDescription>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6">
          <div className="grid gap-1.5">
            <Label htmlFor="invite-first-name">First name</Label>
            <Input
              id="invite-first-name"
              value={data.first_name}
              onChange={(e) => setData("first_name", e.target.value)}
              placeholder="First name"
              autoComplete="given-name"
            />
            {errors.first_name && (
              <p className="text-xs text-destructive">{errors.first_name}</p>
            )}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="invite-last-name">Last name</Label>
            <Input
              id="invite-last-name"
              value={data.last_name}
              onChange={(e) => setData("last_name", e.target.value)}
              placeholder="Last name"
              autoComplete="family-name"
            />
            {errors.last_name && (
              <p className="text-xs text-destructive">{errors.last_name}</p>
            )}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="invite-email">Email address</Label>
            <Input
              id="invite-email"
              type="email"
              value={data.email}
              onChange={(e) => setData("email", e.target.value)}
              placeholder="distributor@example.com"
              autoComplete="email"
            />
            {errors.email && (
              <p className="text-xs text-destructive">{errors.email}</p>
            )}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="invite-region">Region</Label>
            <Select
              value={data.region_id}
              onValueChange={(v) => setData("region_id", v)}
            >
              <SelectTrigger id="invite-region">
                <SelectValue placeholder="Select a region…" />
              </SelectTrigger>
              <SelectContent>
                {allRegions.map((r) => (
                  <SelectItem key={r.id} value={String(r.id)}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.region_id && (
              <p className="text-xs text-destructive">{errors.region_id}</p>
            )}
          </div>

          {errors.base && (
            <p className="text-xs text-destructive">{errors.base}</p>
          )}

          {/* Disable submit until a region is chosen — region is required server-side */}
          <Button type="submit" disabled={processing || !data.region_id} className="mt-2">
            {processing ? "Sending…" : "Send invitation"}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  )
}

// ---------- Assign Region Sheet ----------

function AssignSheet({
  userId,
  open,
  onClose,
  allRegions,
  assignedRegionIds,
  assignUrl,
}: {
  userId: number
  open: boolean
  onClose: () => void
  allRegions: RegionOption[]
  assignedRegionIds: number[]
  assignUrl: string
}) {
  const { errors } = usePage<SharedProps>().props
  const available = allRegions.filter((r) => !assignedRegionIds.includes(r.id))
  const { data, setData, post, processing, reset } = useForm({
    user_id:   String(userId),
    region_id: "",
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post(assignUrl, {
      onSuccess: () => {
        reset()
        onClose()
      },
    })
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="sm:max-w-sm overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Assign region</SheetTitle>
          <SheetDescription>Add this distributor to another region.</SheetDescription>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6">
          {available.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              This distributor is already assigned to all available regions.
            </p>
          ) : (
            <div className="grid gap-1.5">
              <Label htmlFor="assign-region">Region</Label>
              <Select
                value={data.region_id}
                onValueChange={(v) => setData("region_id", v)}
              >
                <SelectTrigger id="assign-region">
                  <SelectValue placeholder="Select a region…" />
                </SelectTrigger>
                <SelectContent>
                  {available.map((r) => (
                    <SelectItem key={r.id} value={String(r.id)}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.region_id && (
                <p className="text-xs text-destructive">{errors.region_id}</p>
              )}
            </div>
          )}
          <Button
            type="submit"
            disabled={processing || !data.region_id || available.length === 0}
          >
            {processing ? "Assigning…" : "Assign region"}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  )
}

// ---------- Remove Region Badge ----------

function RemoveRegionButton({
  distributorId,
  region,
  removeAssignmentUrl,
}: {
  distributorId: number
  region: { id: number; name: string }
  removeAssignmentUrl: string
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          aria-label={`Remove from ${region.name}`}
          className="ml-0.5 rounded-full p-0.5 hover:bg-destructive/20"
        >
          <X className="size-2.5" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove from {region.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This distributor will immediately lose access to properties in {region.name}.
            You can re-assign them at any time.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() =>
              router.delete(removeAssignmentUrl, {
                data: { user_id: distributorId, region_id: region.id },
              })
            }
          >
            Remove
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// ---------- Main Page ----------

export default function DistributorsIndex({ distributors, all_regions, urls }: Props) {
  const [inviteOpen, setInviteOpen]     = useState(false)
  const [inviteKey, setInviteKey]       = useState(0)
  const [assignTarget, setAssignTarget] = useState<DistributorRow | null>(null)
  const [assignOpen, setAssignOpen]     = useState(false)
  const [assignKey, setAssignKey]       = useState(0)

  function openInvite() {
    setInviteKey((k) => k + 1)
    setInviteOpen(true)
  }

  function openAssign(d: DistributorRow) {
    setAssignKey((k) => k + 1)
    setAssignTarget(d)
    setAssignOpen(true)
  }

  function closeAssign() {
    setAssignOpen(false)
    // Keep assignTarget mounted until Sheet finishes closing so the
    // close animation can complete, then clear the data.
    setTimeout(() => setAssignTarget(null), 300)
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Distributors</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Regional managers with access to properties and content in their assigned areas.
          </p>
        </div>
        <Button onClick={openInvite}>
          <UserPlus className="mr-2 size-4" />
          Invite distributor
        </Button>
      </div>

      <div className="rounded-xl border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Regions</TableHead>
              <TableHead className="text-right">Properties</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {distributors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                  No distributors yet. Invite one to get started.
                </TableCell>
              </TableRow>
            ) : (
              distributors.map((d) => (
                <TableRow key={d.id}>
                  <TableCell className="font-medium">
                    <Link href={d.show_url} className="hover:underline">{d.name}</Link>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{d.email}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {d.assigned_regions.map((r) => (
                        <Badge
                          key={r.id}
                          variant="secondary"
                          className="gap-1 pr-1"
                        >
                          {r.name}
                          <RemoveRegionButton distributorId={d.id} region={r} removeAssignmentUrl={urls.remove_assignment} />
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-sm">{d.properties_count}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Assign region to ${d.name}`}
                      onClick={() => openAssign(d)}
                    >
                      <Plus className="mr-1 size-3.5" />
                      Region
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <InviteSheet
        key={inviteKey}
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        allRegions={all_regions}
        inviteUrl={urls.invite}
      />

      <AssignSheet
        key={assignKey}
        userId={assignTarget?.id ?? 0}
        open={assignOpen}
        onClose={closeAssign}
        allRegions={all_regions}
        assignedRegionIds={assignTarget?.assigned_regions.map((r) => r.id) ?? []}
        assignUrl={urls.assign}
      />
    </div>
  )
}

DistributorsIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
